import React, { memo, useCallback, useMemo, useState, useEffect } from 'react';
import { debounce } from 'radash';
import { useDragToAdjust } from './useDragToAdjust';

interface Props {
  value: string;
  onChange: (value: string) => void;
  min: number;
  max: number;
  step: number;
  unit: string;
  defaultValue: string;
  testId: string;
  ariaLabel: string;
  incrementLabel: string;
  decrementLabel: string;
  formatOutput: (value: number) => string;
  parseInput: (value: string) => number;
}

const SpeakerLabelNumberInput = memo(
  ({
    value,
    onChange,
    min,
    max,
    step,
    unit,
    defaultValue,
    testId,
    ariaLabel,
    incrementLabel,
    decrementLabel,
    formatOutput,
    parseInput
  }: Props) => {
    const displayValue = useMemo(() => {
      if (!value || value === 'normal' || value === 'none') {
        return defaultValue;
      }
      const numValue = parseInput(value);
      if (isNaN(numValue)) {
        return defaultValue;
      }
      return Math.round(numValue * 100).toString();
    }, [value, defaultValue, parseInput]);

    const [inputValue, setInputValue] = useState(displayValue);
    const [isOutOfRange, setIsOutOfRange] = useState(false);

    // Update local state when prop changes
    useEffect(() => {
      setInputValue(displayValue);
      setIsOutOfRange(false);
    }, [displayValue]);

    const handleDragChange = useCallback(
      (numValue: number) => {
        setInputValue(numValue.toString());
        onChange(formatOutput(numValue));
      },
      [onChange, formatOutput]
    );

    const { handleMouseDown: handleDragMouseDown, cursor: dragCursor } = useDragToAdjust({
      min,
      max,
      step,
      value: parseFloat(inputValue) || parseFloat(defaultValue),
      onChange: handleDragChange,
      direction: 'vertical'
    });

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setInputValue(newValue);

        // Check if value is out of range for visual feedback
        const numValue = parseFloat(newValue);
        if (newValue !== '' && !isNaN(numValue)) {
          setIsOutOfRange(numValue < min || numValue > max);

          // Only call onChange for valid values within range
          if (numValue >= min && numValue <= max) {
            onChange(formatOutput(numValue));
          }
        } else {
          setIsOutOfRange(false);
        }
      },
      [onChange, min, max, formatOutput]
    );

    const handleBlur = useCallback(() => {
      const numValue = parseFloat(inputValue);

      // If input is empty or invalid, reset to current value
      if (inputValue === '' || isNaN(numValue)) {
        setInputValue(displayValue);
        setIsOutOfRange(false);
        return;
      }

      // Clamp value to valid range and apply
      const clampedValue = Math.max(min, Math.min(max, numValue));
      if (clampedValue !== numValue) {
        setInputValue(clampedValue.toString());
        onChange(formatOutput(clampedValue));
      }
      setIsOutOfRange(false);
    }, [inputValue, displayValue, onChange, min, max, formatOutput]);

    // Debounced increment/decrement functions to prevent rage clicking
    const debouncedIncrement = useMemo(
      () =>
        debounce({ delay: 100 }, () => {
          const numValue = parseFloat(inputValue) || parseFloat(defaultValue);
          const newValue = Math.min(max, numValue + step);
          const roundedValue = step < 1 ? Math.round(newValue * 10) / 10 : newValue;
          setInputValue(roundedValue.toString());
          onChange(formatOutput(roundedValue));
        }),
      [inputValue, defaultValue, max, step, onChange, formatOutput]
    );

    const debouncedDecrement = useMemo(
      () =>
        debounce({ delay: 100 }, () => {
          const numValue = parseFloat(inputValue) || parseFloat(defaultValue);
          const newValue = Math.max(min, numValue - step);
          const roundedValue = step < 1 ? Math.round(newValue * 10) / 10 : newValue;
          setInputValue(roundedValue.toString());
          onChange(formatOutput(roundedValue));
        }),
      [inputValue, defaultValue, min, step, onChange, formatOutput]
    );

    const handleIncrement = useCallback(() => {
      debouncedIncrement();
    }, [debouncedIncrement]);

    const handleDecrement = useCallback(() => {
      debouncedDecrement();
    }, [debouncedDecrement]);

    return (
      <div
        className={`flex h-6 w-full flex-row items-center justify-center gap-0.5 rounded px-2 py-1.5 transition-colors ${
          isOutOfRange ? 'border border-red-200 bg-red-50' : 'bg-gray-100'
        }`}
      >
        <div
          className="w-full"
          style={{ cursor: dragCursor }}
          onMouseDown={handleDragMouseDown}
          title={`Between ${min} to ${max}. Drag to adjust.`}
        >
          <input
            data-testid={testId}
            type="number"
            min={min}
            max={max}
            step={step}
            value={inputValue}
            onChange={handleChange}
            onBlur={handleBlur}
            aria-label={ariaLabel}
            aria-describedby={`${testId}-unit`}
            aria-invalid={isOutOfRange}
            style={{ cursor: dragCursor }}
            className={`min-w-0 !border-none bg-transparent p-0 text-center text-2xs font-medium !ring-0 transition-colors [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none ${
              isOutOfRange ? 'text-red-600' : 'text-gray-700'
            }`}
            placeholder={defaultValue}
          />
          <span
            id={`${testId}-unit`}
            style={{ cursor: dragCursor }}
            className={`text-2xs font-medium transition-colors ${isOutOfRange ? 'text-red-400' : 'text-gray-500'}`}
            aria-hidden="true"
          >
            {unit}
          </span>
        </div>
        <div className="flex flex-col">
          <button
            data-testid={`${testId}-increment`}
            type="button"
            onClick={handleIncrement}
            className="flex h-2 w-3 items-center justify-center text-gray-400 hover:text-gray-600"
            aria-label={incrementLabel}
          >
            <svg width="8" height="4" viewBox="0 0 8 4" fill="currentColor">
              <path d="M4 0L0 4h8L4 0z" />
            </svg>
          </button>
          <button
            data-testid={`${testId}-decrement`}
            type="button"
            onClick={handleDecrement}
            className="flex h-2 w-3 items-center justify-center text-gray-400 hover:text-gray-600"
            aria-label={decrementLabel}
          >
            <svg width="8" height="4" viewBox="0 0 8 4" fill="currentColor">
              <path d="M4 4L8 0H0l4 4z" />
            </svg>
          </button>
        </div>
      </div>
    );
  }
);

export default SpeakerLabelNumberInput;
